'use client';
import React from 'react';
import { ClearButton } from './ClearButton';
import { DropdownPanel } from './DropdownPanel';
import { TriggerButton } from './TriggerButton';

export interface SearchableDropdownOption<T extends string = string> {
  value: T;
  label: string;
}

export type SearchableDropdownProps<T extends string = string> = {
  options: SearchableDropdownOption<T>[];
  className?: string;
  labelPrefix?: string;
  placeholder?: string;
  emptyDisplayValue?: string;
  allowClear?: boolean;
  onSearchChange?: (value: string) => void;
  searchValueExternallyControlled?: string;
  isOptionsLoading?: boolean;
  optionsError?: string | Error | null;
  hideSearch?: boolean;
  position?: 'left' | 'center' | 'right';
  disabled?: boolean;
} & (
  | {
      multiSelect?: false;
      selectedValue: T | null;
      onChange: (selectedValue: T | null) => void;
    }
  | {
      multiSelect: true;
      selectedValue: T[];
      onChange: (selectedValue: T[]) => void;
    }
);

export function SearchableDropdown<T extends string = string>(props: SearchableDropdownProps<T>) {
  const {
    options,
    className,
    labelPrefix = 'Filter:',
    placeholder = 'Search...',
    emptyDisplayValue = 'All',
    allowClear = true,
    onSearchChange,
    searchValueExternallyControlled,
    isOptionsLoading,
    optionsError,
    hideSearch = false,
    position = 'left',
    disabled = false,
  } = props;

  const [selectedLabel, setSelectedLabel] = React.useState<string | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (onSearchChange) {
          onSearchChange('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onSearchChange]);

  const handleClose = () => {
    if (onSearchChange) {
      onSearchChange('');
    }
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleOptionClick = (optionValue: T, optionLabel: string) => {
    if (isMultiSelect(props)) {
      if (props.selectedValue.includes(optionValue)) {
        props.onChange(props.selectedValue.filter(v => v !== optionValue));
        return;
      }
      props.onChange([...props.selectedValue, optionValue]);
      return;
    }

    props.onChange(optionValue);
    setSelectedLabel(optionLabel);
    handleClose();
  };

  const handleClear = () => {
    if (isMultiSelect(props)) {
      props.onChange([]);
      return;
    }
    props.onChange(null);
    onSearchChange?.('');
    buttonRef.current?.focus();
  };

  const showClearButton =
    allowClear &&
    (isMultiSelect(props) ? props.selectedValue.length > 0 : props.selectedValue !== null);

  const getDisplayValue = (): string => {
    if (isMultiSelect(props)) {
      if (props.selectedValue.length === 0) return emptyDisplayValue;
      if (props.selectedValue.length === 1) {
        const found = options.find(opt => opt.value === props.selectedValue[0]);
        return found?.label ?? props.selectedValue[0];
      }
      return `${props.selectedValue.length} selected`;
    }

    if (props.selectedValue == null) return emptyDisplayValue;
    const found = options.find(opt => opt.value === props.selectedValue);
    return found?.label ?? selectedLabel ?? props.selectedValue;
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={`inline-flex items-stretch ${className || ''}`}>
      <div className="relative flex">
        <TriggerButton
          ref={buttonRef}
          labelPrefix={labelPrefix}
          displayValue={getDisplayValue()}
          isFullyRounded={!showClearButton}
          onClick={handleToggle}
          disabled={disabled}
        />
        <DropdownPanel
          isShown={isOpen}
          position={position}
          hideSearch={hideSearch}
          placeholder={placeholder}
          options={options}
          selectedValue={props.selectedValue}
          isMulti={isMultiSelect(props)}
          onOptionClick={handleOptionClick}
          onClose={handleClose}
          isLoading={isOptionsLoading}
          error={optionsError}
          onSearchChange={onSearchChange}
          searchValueExternallyControlled={searchValueExternallyControlled}
        />
      </div>
      <ClearButton isVisible={showClearButton} labelPrefix={labelPrefix} onClear={handleClear} />
    </div>
  );
}

const isMultiSelect = (
  props: SearchableDropdownProps,
): props is Extract<SearchableDropdownProps, { multiSelect: true }> => {
  return props.multiSelect === true;
};
